import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { TriangleAlert } from 'lucide-react';
import { Input } from '../../../../components/ui/input';
import {
  contrastAgainstEditorBackground,
  type CustomColorPickerRow as CustomColorPickerRowDef,
} from '../../../../core/theme/customEditorColors';

const HEX_RE = /^#[0-9a-f]{6}$/i;

function normalizeHexInput(raw: string): string | null {
  const trimmed = raw.trim();
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  return HEX_RE.test(withHash) ? withHash.toLowerCase() : null;
}

interface CustomColorPickerRowProps {
  picker: CustomColorPickerRowDef;
  /** Current DRAFT hex for this picker's first token — the caller (`CustomEditorColorsPanel`) owns the draft. */
  value: string;
  /** The variant being edited — drives the contrast check against this variant's own editor background. */
  variant: 'light' | 'dark';
  /** Commits `hex` to every token this row covers, in the draft. Never writes the store. */
  onChange: (hex: string) => void;
}

/**
 * One row of the Custom editor colors panel (staged-draft dialog iteration):
 * a native color swatch input (live-previews while dragging, commits on
 * release), a mono hex text field (commits on blur/Enter, reverts on
 * invalid), and a low-contrast warning. Fully controlled — every commit goes
 * through `onChange` into the dialog's draft state; nothing here touches the
 * store or fires analytics (analytics for a committed edit fire once, on the
 * dialog's Confirm, by diffing the draft against its opening snapshot).
 *
 * `<input type="color">` fires the DOM 'input' event continuously while its
 * picker dialog is open (drag preview) and exactly one 'change' when it
 * closes (final value) — React's `onChange` prop normalizes both to
 * 'input', which would lose that distinction, so this wires the raw DOM
 * events via a ref instead: debounce the drag (~100ms trailing), commit on
 * release without waiting for the debounce.
 */
export function CustomColorPickerRow({ picker, value, variant, onChange }: CustomColorPickerRowProps) {
  const [hexInputValue, setHexInputValue] = useState(value);
  const colorInputRef = useRef<HTMLInputElement>(null);

  // Always-fresh `onChange` for the DOM listeners below, which are wired
  // once on mount rather than re-wired every render (e.g. if the dialog's
  // variant toggle flips while a picker dialog happens to be open).
  const latestOnChangeRef = useRef(onChange);
  latestOnChangeRef.current = onChange;

  const applyHex = useCallback((hex: string) => latestOnChangeRef.current(hex), []);
  const debouncedApply = useDebouncedCallback(applyHex, 100);

  // Sync the color input + hex field when `value` changes for a reason
  // other than this row's own edit (e.g. "Copy from scheme", or the
  // light/dark variant switching).
  useEffect(() => {
    const el = colorInputRef.current;
    if (el && el.value.toLowerCase() !== value.toLowerCase()) el.value = value;
    setHexInputValue(value);
  }, [value]);

  useEffect(() => {
    const el = colorInputRef.current;
    if (!el) return;

    const handleInput = (event: Event) => {
      const hex = (event.target as HTMLInputElement).value;
      setHexInputValue(hex);
      debouncedApply(hex);
    };
    const handleChange = (event: Event) => {
      const hex = (event.target as HTMLInputElement).value;
      debouncedApply.cancel();
      applyHex(hex);
    };

    el.addEventListener('input', handleInput);
    el.addEventListener('change', handleChange);
    return () => {
      el.removeEventListener('input', handleInput);
      el.removeEventListener('change', handleChange);
    };
  }, [debouncedApply, applyHex]);

  function commitHexInput() {
    const normalized = normalizeHexInput(hexInputValue);
    if (!normalized) {
      setHexInputValue(value); // revert — invalid text never reaches the draft
      return;
    }
    debouncedApply.cancel();
    applyHex(normalized);
    setHexInputValue(normalized);
  }

  const lowContrast =
    HEX_RE.test(hexInputValue) && contrastAgainstEditorBackground(hexInputValue, variant === 'dark') < 3;

  return (
    <div className="flex items-center gap-2">
      <span className="w-24 shrink-0 text-xs text-muted-foreground">{picker.label}</span>
      <input
        ref={colorInputRef}
        type="color"
        defaultValue={value}
        aria-label={`${picker.label} color`}
        data-testid={`custom-color-${picker.id}-input`}
        className="h-7 w-9 shrink-0 cursor-pointer rounded-md border border-input bg-background p-0.5"
      />
      <Input
        size="xs"
        className="w-24 shrink-0 font-mono"
        value={hexInputValue}
        onChange={(event) => setHexInputValue(event.target.value)}
        onBlur={commitHexInput}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            commitHexInput();
            (event.target as HTMLInputElement).blur();
          }
        }}
        data-testid={`custom-color-${picker.id}-hex`}
      />
      <span className="flex h-4 w-4 shrink-0 items-center justify-center">
        {lowContrast && (
          <span title="Low contrast against the editor background">
            <TriangleAlert
              className="h-4 w-4 text-warning"
              data-testid={`custom-color-${picker.id}-warning`}
            />
          </span>
        )}
      </span>
    </div>
  );
}
